import { ViewContainerRef, inject, Injectable, Injector } from '@angular/core';

import type { Observable } from 'rxjs';

import { I18nService } from '@core/i18n-legacy';

import type { DialogConfig, DialogConfigOptions } from '../dialog/dialog-config';
import { buildDialogConfig, DialogWidth } from '../dialog/dialog-config';
import { DialogOpenerService } from '../dialog/dialog-opener.service';
import type { DialogRef } from '../dialog/dialog-ref';
import { DialogComponent, DialogComponentI, DialogData } from '../dialog/dialog.component';
import { OVERLAY_LEVEL } from '../dialog/providers';
import { ConfirmDialogComponent } from './confirm-dialog/confirm-dialog.component';
import { ConfirmDialogVariant } from './dialogs.types';

export type DialogConfirmVariant = 'success' | 'error' | 'information' | 'warning';

export interface DialogConfirmOptions extends DialogConfigOptions {
  title?: string;
  message?: string;
  footerMessage?: string;
  variant?: DialogConfirmVariant | string;
  isCancelable?: boolean;
  safeFooter?: boolean;
  checkboxes?: Array<{ title: string; value?: boolean }>;
  list?: string[];
  emphasizeLastCheckbox?: boolean;
  agreeLabel?: string;
  cancelLabel?: string;
  tags?: string[];
  showMoreLabel?: string;
  showLessLabel?: string;
  confirmActionsLayout?: 'end' | 'space-between';
}

@Injectable({ providedIn: 'root' })
export class DialogService {
  private defaultErrorConfig: Partial<DialogConfirmOptions> = {
    variant: ConfirmDialogVariant.Error,
    isCancelable: true,
    safeFooter: true,
    title: this.i18nService.translate('rtu.common.confirm_delete'),
    agreeLabel: this.i18nService.translate('rtu.common.delete'),
    cancelLabel: this.i18nService.translate('rtu.common.cancel'),
    showMoreLabel: this.i18nService.translate('rtu.common.show_more'),
    showLessLabel: this.i18nService.translate('rtu.common.show_less'),
    emphasizeLastCheckbox: true,
  };

  private defaultInformationConfig: Partial<DialogConfirmOptions> = {
    variant: ConfirmDialogVariant.Information,
    isCancelable: true,
    safeFooter: false,
    title: this.i18nService.translate('rtu.common.confirm_action'),
    agreeLabel: this.i18nService.translate('rtu.common.confirm'),
    cancelLabel: this.i18nService.translate('rtu.common.cancel'),
    showMoreLabel: this.i18nService.translate('rtu.common.show_more'),
    showLessLabel: this.i18nService.translate('rtu.common.show_less'),
    emphasizeLastCheckbox: false,
  };

  private defaultWarningConfig: Partial<DialogConfirmOptions> = {
    variant: ConfirmDialogVariant.Warning,
    isCancelable: true,
    safeFooter: false,
    title: this.i18nService.translate('rtu.common.confirm_action'),
    agreeLabel: this.i18nService.translate('rtu.common.discard'),
    cancelLabel: this.i18nService.translate('rtu.common.cancel'),
    showMoreLabel: this.i18nService.translate('rtu.common.show_more'),
    showLessLabel: this.i18nService.translate('rtu.common.show_less'),
    emphasizeLastCheckbox: false,
  };

  constructor(
    private dialogOpener: DialogOpenerService,
    private injector: Injector,
    private i18nService: I18nService,
  ) {}

  public error(
    options: DialogConfirmOptions,
    viewContainerRef?: ViewContainerRef,
  ): Observable<boolean> {
    return this.confirm(
      { ...this.getDefaultConfig(ConfirmDialogVariant.Error), ...options },
      viewContainerRef,
    );
  }

  public information(
    options: DialogConfirmOptions,
    viewContainerRef?: ViewContainerRef,
  ): Observable<boolean> {
    return this.confirm(
      { ...this.getDefaultConfig(ConfirmDialogVariant.Information), ...options },
      viewContainerRef,
    );
  }

  public warning(
    options: DialogConfirmOptions,
    viewContainerRef?: ViewContainerRef,
  ): Observable<boolean> {
    return this.confirm(
      { ...this.getDefaultConfig(ConfirmDialogVariant.Warning), ...options },
      viewContainerRef,
    );
  }

  /**
   * @deprecated use information(), warning() or error() methods instead
   */

  public confirm(
    options: DialogConfirmOptions,
    viewContainerRef?: ViewContainerRef | null,
  ): Observable<boolean> {
    const _options = {
      ...this.getDefaultConfig(options.variant!),
      ...Object.entries(options).reduce(
        (acc, [key, value]) => (value === undefined ? acc : { ...acc, [key]: value }),
        {},
      ),
    };

    const {
      title,
      message,
      variant,
      isCancelable,
      safeFooter,
      checkboxes,
      emphasizeLastCheckbox,
      agreeLabel,
      cancelLabel,
      tags = [],
      list = [],
      showMoreLabel,
      showLessLabel,
      confirmActionsLayout,
      footerMessage,
      ...dialogOptions
    } = _options;
    const dialogData = {
      title,
      message,
      variant,
      isCancelable,
      safeFooter,
      checkboxes,
      emphasizeLastCheckbox,
      tags,
      list,
      footerMessage,
      ...(confirmActionsLayout ? { confirmActionsLayout } : {}),
      ...(agreeLabel ? { agreeLabel } : {}),
      ...(cancelLabel ? { cancelLabel } : {}),
      ...(showMoreLabel ? { showMoreLabel } : {}),
      ...(showLessLabel ? { showLessLabel } : {}),
    };
    const dialogConfig = {
      width: DialogWidth.SmallXX,
      disableClose: !isCancelable,
      ...dialogOptions,
    };

    return this.openCustom(ConfirmDialogComponent, dialogConfig, dialogData, viewContainerRef)
      .afterClosed;
  }

  /**
   * @deprecated use .openDialog() instead. openDialog is better typed and provides more modern API
   * @param cmpType
   * @param options
   * @param data
   * @param viewContainerRef
   */
  public openCustom<
    DC extends DialogComponentI<DialogData, DialogResult>,
    DialogData,
    DialogResult = any,
  >(
    cmpType: new (...args: any[]) => DC,
    options: DialogConfigOptions | null,
    data?: DialogData | null,
    viewContainerRef?: ViewContainerRef | null,
  ): DialogRef<DC, DialogResult> {
    return this.custom(cmpType, options, data || null, viewContainerRef);
  }

  /** @nuggets
   * @desc new typesafe way to open and close dialogs
   * @example
   * ```typescript
   * class MyDialogComponent extends DialogComponent<MyDialogData, MyDialogResult> {
   *  public id = this.dialogData.id;
   *
   *  public close(result: MyDialogResult): void {
   *    this.dialogRef.close(result);
   *  }
   * }
   * const dialogRef = dialogService.openDialog(MyDialogComponent, { myData: 'data' });
   * dialogRef.afterClosed.subscribe((result: MyDialogResult) => {
   *  console.log(result);
   *  });
   * ```
   * `DialogComponent` already provides dialog data as a property on the class with correct data. The same
   * with dialog reference, which will typecheck that the result is of the correct type
   */
  /**
   * @param cmp - component extending DialogComponent class. It will have dialogData defined as a property on initialization
   * @param data - data to be passed to the dialog component
   * @param options - dialog configuration options
   * @param viewContainerRef - view container reference to attach the dialog to
   */
  // additional declaration for dialog without the data
  public openDialog<
    DC extends DialogComponent<null | void | undefined, any>,
    DialogResult = DC extends DialogComponent<any, infer R> ? R : never,
  >(
    cmp: new (...args: any[]) => DC,
    data?: null | void,
    options?: DialogConfigOptions | null,
    viewContainerRef?: ViewContainerRef | null,
  ): DialogRef<DC, DialogResult>;
  public openDialog<
    DC extends DialogComponent<any, any>,
    DialogResult = DC extends DialogComponent<any, infer R> ? R : never,
  >(
    cmp: new (...args: any[]) => DC,
    data: DC extends DialogComponent<infer R> ? R : never,
    options?: DialogConfigOptions | null,
    viewContainerRef?: ViewContainerRef | null,
  ): DialogRef<DC, DialogResult>;
  public openDialog<
    DC extends DialogComponent<any>,
    DialogResult = DC extends DialogComponent<any, infer R> ? R : never,
  >(
    cmp: new (...args: any[]) => DC,
    data: DC extends DialogComponent<infer R> ? R : never,
    options: DialogConfigOptions | null = null,
    viewContainerRef: ViewContainerRef | null = null,
  ): DialogRef<DC, DialogResult | undefined> {
    return this.custom(cmp, options, data ?? null, viewContainerRef);
  }

  /**
   * @deprecated use .openCustom() instead (if only the dialog implements DialogComponentI)
   */
  public custom<DC>(
    cmpType: new (...args: any[]) => DC,
    options: DialogConfigOptions | null | undefined,
    data: any,
    viewContainerRef?: ViewContainerRef | null,
  ): DialogRef<DC> {
    const dialogConfig = buildDialogConfig({
      ...options,
      viewContainerRef,
      data,
    });
    const dialogRef = this.dialogOpener.open(cmpType, {
      ...dialogConfig,
      injector: this.getDialogInjector(dialogConfig),
    });
    this.handleDialogOpen();
    dialogRef.afterClosed.subscribe(() => this.handleDialogClose());
    return dialogRef;
  }

  public isTop(dialog: DialogRef): boolean {
    return this.dialogOpener.isTop(dialog);
  }

  public hasDialogsOpen(): boolean {
    return this.dialogOpener.hasDialogsOpen;
  }

  public closeAll(): Observable<any> {
    return this.dialogOpener.closeAll();
  }

  private handleDialogOpen(): void {
    document.body.style.overflowX = 'hidden';
  }

  private handleDialogClose(): void {
    // NOTE monkeypatch, we should check if any overlay is open in more sane way.
    const overlays = Array.from(
      document.body.querySelectorAll('.rt-overlay-container .rt-global-overlay-wrapper'),
    );
    if (overlays.length === 0) {
      document.body.style.overflowX = 'auto';
    }
  }

  private getDefaultConfig(variant: ConfirmDialogVariant | string): Partial<DialogConfirmOptions> {
    switch (variant) {
      case ConfirmDialogVariant.Error:
        return this.defaultErrorConfig;
      case ConfirmDialogVariant.Information:
        return this.defaultInformationConfig;
      case ConfirmDialogVariant.Warning:
        return this.defaultWarningConfig;
      default:
        return this.defaultInformationConfig;
    }
  }

  // Logic is here because dialogOpener will be replaced by CDK
  private getDialogInjector(dialogConfig: DialogConfig) {
    const injector =
      dialogConfig.injector || dialogConfig.viewContainerRef?.injector || this.injector;
    return Injector.create({
      parent: injector,
      providers: [
        {
          provide: OVERLAY_LEVEL,
          useFactory: () => {
            const parent = inject(OVERLAY_LEVEL, { optional: true, skipSelf: true });
            return parent !== null ? parent + 1 : 0;
          },
        },
        {
          provide: DialogData,
          useValue: dialogConfig.data,
        },
      ],
    });
  }
}
